/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useRef, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { isUserLicensed } from '@bb/users';

import { AuthContext, AddListingToTourModalContext } from 'contexts';
import { featureEnabled } from 'utils';
import { Button, Tag } from '@brokerbay/elements-web';
import { Button as ButtonV2, Color } from '@bb/elements-web-rebranding';
import Icon from '../Icon';
import { Menu } from '../Menu';
import MenuItem, { MenuItemProps } from '../Menu/MenuItem';
import Dropdown from '../Dropdown';
import Tooltip, { TooltipProps } from '../Tooltip';
import ListingSummaryProps from '../../types/listingSummaryPropsType';

import { StyledButton, StyledButtonWrapper } from './StyledComponents';
import { TOURS_DISABLED_SHOWING_STATUS } from '../../Router/pages/TourEditPage/constants';

export const ActionTooltip: React.FC<TooltipProps> = ({
  children,
  ...props
}) => (
  <Tooltip placement="bottom" mouseLeaveDelay={0} {...props}>
    {children}
  </Tooltip>
);

type ScheduleButtonDropdownProps = {
  handleScheduleClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  showScheduleBtn?: boolean;
  listing: ListingSummaryProps;
  noMoreShowings: boolean;
  external?: boolean;
};

const ScheduleButtonDropdown: React.FC<ScheduleButtonDropdownProps> = ({
  handleScheduleClick,
  showScheduleBtn,
  listing,
  noMoreShowings,
  external,
}) => {
  const wrapperRef = useRef(null);
  const { auth } = useContext(AuthContext);
  const { setModalVisible, setListing } =
    useContext(AddListingToTourModalContext) || {};

  const onScheduleClick: MenuItemProps['onClick'] = e => {
    handleScheduleClick(
      e.domEvent as React.MouseEvent<HTMLElement, MouseEvent>,
    );
  };

  const handleAddToTourClick = () => {
    setListing(listing);
    setModalVisible(true);
  };

  const overlay = (
    <StyledMenu>
      <StyledMenuItem
        key="schedule"
        onClick={onScheduleClick}
        disabled={!showScheduleBtn}
      >
        Schedule showing
      </StyledMenuItem>
      <StyledMenuItem
        key="tour"
        onClick={handleAddToTourClick}
        disabled={noMoreShowings}
      >
        Add to tour
      </StyledMenuItem>
    </StyledMenu>
  );

  const showScheduleDropdown = useMemo(
    () =>
      !external &&
      isUserLicensed(auth) &&
      !noMoreShowings &&
      !TOURS_DISABLED_SHOWING_STATUS.includes(listing.availability),
    [],
  );

  return (
    <>
      {featureEnabled('showingsMadeEasyFeature') && !external ? (
        <>
          {showScheduleBtn && !listing.org?.isUnclaimed ? (
            <StyledButtonContainer>
              {featureEnabled('supraRebrandingFeature', 1) ? (
                <ButtonV2 onClick={handleScheduleClick} dataCy="showing-button">
                  {listing.autoConfirm ? `Book` : `Request`} Showing
                </ButtonV2>
              ) : (
                <Button onClick={handleScheduleClick} dataCy="showing-button">
                  {listing.autoConfirm ? `Book` : `Request`} Showing
                </Button>
              )}
              {auth &&
                isUserLicensed(auth) &&
                (featureEnabled('supraRebrandingFeature', 1) ? (
                  <ButtonV2
                    type="secondary"
                    color="black"
                    disabled={
                      noMoreShowings ||
                      TOURS_DISABLED_SHOWING_STATUS.includes(
                        listing.availability,
                      )
                    }
                    onClick={handleAddToTourClick}
                    dataCy="add-tour-button"
                    styleOverride={{ borderColor: Color.BEERUS }}
                  >
                    Add to tour
                  </ButtonV2>
                ) : (
                  <Button
                    type="secondary"
                    disabled={
                      noMoreShowings ||
                      TOURS_DISABLED_SHOWING_STATUS.includes(
                        listing.availability,
                      )
                    }
                    onClick={handleAddToTourClick}
                    dataCy="add-tour-button"
                  >
                    Add to tour
                  </Button>
                ))}
            </StyledButtonContainer>
          ) : (
            <Tag
              dataCy="booking-unavailable-tag"
              icon="infoCircleOutlined"
              style={{ fontSize: '16px' }}
            >
              Online booking unavailable
            </Tag>
          )}
        </>
      ) : showScheduleDropdown ? (
        <ActionTooltip
          title={
            !showScheduleBtn &&
            'Online showing booking is not currently available for this listing'
          }
          data-cy="schedule-tooltip"
        >
          <StyledButtonWrapper ref={wrapperRef}>
            <Dropdown
              trigger={['click']}
              dropdownRender={() => overlay}
              getPopupContainer={() => wrapperRef.current}
              disabled={!showScheduleBtn}
            >
              <StyledButton
                icon={<Icon type="calendar" width={16} />}
                data-cy="schedule-dropdown-button"
                disabled={!showScheduleBtn}
              >
                Schedule
              </StyledButton>
            </Dropdown>
          </StyledButtonWrapper>
        </ActionTooltip>
      ) : (
        <ActionTooltip
          title={
            !showScheduleBtn &&
            'Online showing booking is not currently available for this listing'
          }
          data-cy="schedule-tooltip"
        >
          <StyledButtonWrapper ref={wrapperRef}>
            <StyledButton
              onClick={handleScheduleClick}
              icon={<Icon type="calendar" width={16} />}
              disabled={!showScheduleBtn}
              data-cy="schedule-button"
            >
              Schedule
            </StyledButton>
          </StyledButtonWrapper>
        </ActionTooltip>
      )}
    </>
  );
};

export default ScheduleButtonDropdown;

const StyledButtonContainer = styled.div`
  display: flex;
  gap: 8px;

  && button {
    min-width: min-content;
  }
`;

const StyledMenu = styled(Menu)`
  && {
    border-radius: 0px;
    overflow-y: hidden;
    padding: 0;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  &&& {
    border-radius: 0px;
  }
`;
